import { useState, useEffect, useCallback } from 'react';
import { inviteApi } from '@/api/inviteApi';
import type { InviteData } from '@/types/invite';
import type { CreateInviteRequest } from '@/types/api';
import { getErrorMessage, logError } from '@/utils/error';
import { logger } from '@/utils/logger';

/**
 * Хук для работы с приглашениями
 */
export const useInvite = (inviteId?: string) => {
  const [invite, setInvite] = useState<InviteData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Загружает приглашение по ID
   */
  const fetchInvite = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const data = await inviteApi.getById(id);
      logger.debug('[useInvite] Получено приглашение:', {
        id: data.id,
        template: data.template,
        brideName: data.brideName,
        groomName: data.groomName,
      });
      setInvite(data);
      return { data, error: null };
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      logError(err, 'useInvite.fetchInvite');
      setError(errorMessage);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Создает новое приглашение
   */
  const createInvite = useCallback(async (data: CreateInviteRequest) => {
    setLoading(true);
    setError(null);

    try {
      const result = await inviteApi.create(data);
      return { data: result, error: null };
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      logError(err, 'useInvite.createInvite');
      setError(errorMessage);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Сбрасывает состояние
   */
  const reset = useCallback(() => {
    setInvite(null);
    setError(null);
    setLoading(false);
  }, []);

  // Автоматическая загрузка при изменении inviteId
  useEffect(() => {
    if (inviteId) {
      fetchInvite(inviteId);
    }
  }, [inviteId, fetchInvite]);

  return {
    invite,
    loading,
    error,
    fetchInvite,
    createInvite,
    reset,
  };
};
